import { useState, useEffect } from "react";
import api from "../lib/api";
import { Th, Td, Empty } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Database } from "lucide-react";

const TABS = [
  { key: "items", label: "Items / Products", fields: ["name", "category", "unit", "min_level"] },
  { key: "departments", label: "Departments", fields: ["name"] },
  { key: "users", label: "Procurement Users", fields: ["name"] },
];
const LABELS = { name: "Name", category: "Category", unit: "Unit", min_level: "Min Stock Level" };

export default function Masters() {
  const [tab, setTab] = useState("items");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false); const [editId, setEditId] = useState(null); const [form, setForm] = useState({ status: "Active" });
  const current = TABS.find((t) => t.key === tab);

  const load = () => api.get(`/masters/${tab}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [tab]);

  const openCreate = () => { setEditId(null); setForm({ status: "Active" }); setOpen(true); };
  const openEdit = (r) => { setEditId(r.id); setForm({ ...r }); setOpen(true); };
  const submit = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    const p = { ...form }; if (p.min_level != null) p.min_level = Number(p.min_level || 0);
    if (editId) { await api.put(`/masters/${tab}/${editId}`, p); toast.success("Updated"); } else { await api.post(`/masters/${tab}`, p); toast.success("Added"); }
    setOpen(false); setEditId(null); load();
  };
  const remove = async (id) => { await api.delete(`/masters/${tab}/${id}`); toast.success("Deleted"); load(); };
  const toggleStatus = async (r) => { await api.put(`/masters/${tab}/${r.id}`, { status: r.status === "Active" ? "Inactive" : "Active" }); load(); };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Master Data</h1>
          <p className="text-brand-slate text-sm mt-1">Editable lists — no hardcoded values. Vendors are managed under Vendor Directory.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-master-button" onClick={openCreate} className="gap-2 bg-brand-teal hover:bg-brand-tealdark"><Plus className="w-4 h-4" /> Add {current.label}</Button></DialogTrigger>
          <DialogContent className="max-w-md"><DialogHeader><DialogTitle className="font-heading">{editId ? "Edit" : "Add"} — {current.label}</DialogTitle><DialogDescription>Add, edit, activate or deactivate master records.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {current.fields.map((f) => (
                <div key={f} className={f === "name" ? "col-span-2" : ""}><Label>{LABELS[f]}</Label>
                  <Input data-testid={`master-${f}`} type={f === "min_level" ? "number" : "text"} value={form[f] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [f]: e.target.value }))} /></div>
              ))}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="master-submit" onClick={submit} className="bg-brand-teal hover:bg-brand-tealdark">{editId ? "Save" : "Add"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`master-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`text-sm font-semibold px-4 py-2 rounded-lg border transition-colors ${tab === t.key ? "bg-navy text-white border-navy" : "border-border text-brand-slate hover:bg-slate-50"}`}>{t.label}</button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="bg-slate-50 border-b border-border"><tr>{[...current.fields.map((f) => LABELS[f]), "Status", "Actions"].map((h) => <Th key={h}>{h}</Th>)}</tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={current.fields.length + 2}><Empty icon={Database} msg="No records" /></td></tr>
              : rows.map((r) => (
                <tr key={r.id} data-testid="master-row" className="border-b border-border/60 hover:bg-slate-50">
                  {current.fields.map((f) => <Td key={f} className={f === "name" ? "font-medium" : "text-slate-600"}>{r[f] ?? "—"}</Td>)}
                  <Td><button data-testid="master-status-toggle" onClick={() => toggleStatus(r)} className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${r.status === "Active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{r.status}</button></Td>
                  <Td><div className="flex gap-2">
                    <button data-testid="master-edit" onClick={() => openEdit(r)} className="text-slate-400 hover:text-brand-teal"><Pencil className="w-4 h-4" /></button>
                    <button data-testid="master-delete" onClick={() => remove(r.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                  </div></Td>
                </tr>
              ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
